import { useCallback, useEffect, useRef, useState } from 'react'

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'recorded' | 'error'

export type RecorderErrorKind = 'denied' | 'no-device' | 'in-use' | 'unsupported' | 'unknown'

export interface VoiceRecording {
  blob: Blob
  mimeType: string
  durationMs: number
}

export const MAX_RECORDING_MS = 30_000

export const MIN_RECORDING_MS = 4_000

const pickMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return candidates.find((type) => MediaRecorder.isTypeSupported(type))
}

const baseMimeType = (mimeType: string): string => mimeType.split(';')[0]

const toErrorKind = (error: unknown): RecorderErrorKind => {
  const name = (error as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no-device'
  if (name === 'NotReadableError') return 'in-use'
  return 'unknown'
}

export const useVoiceRecorder = () => {
  const [state, setState] = useState<RecorderState>('idle')
  const [durationMs, setDurationMs] = useState(0)
  const [errorKind, setErrorKind] = useState<RecorderErrorKind | null>(null)
  const [recording, setRecording] = useState<VoiceRecording | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startedAtRef = useRef(0)

  const isSupported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined'

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current)
    timerRef.current = null
    stopTimeoutRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  useEffect(() => cleanup, [cleanup])

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) {
      setErrorKind('unsupported')
      setState('error')
      return
    }
    setErrorKind(null)
    setRecording(null)
    setState('requesting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      recorderRef.current = recorder
      chunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const type = baseMimeType(mimeType ?? 'audio/webm')
        const blob = new Blob(chunksRef.current, { type })
        setRecording({ blob, mimeType: type, durationMs: Date.now() - startedAtRef.current })
        setState('recorded')
        cleanup()
      }

      startedAtRef.current = Date.now()
      setDurationMs(0)
      recorder.start()
      setState('recording')
      timerRef.current = setInterval(() => setDurationMs(Date.now() - startedAtRef.current), 200)
      stopTimeoutRef.current = setTimeout(stop, MAX_RECORDING_MS)
    } catch (error) {
      cleanup()
      setErrorKind(toErrorKind(error))
      setState('error')
    }
  }, [cleanup, isSupported, stop])

  const reset = useCallback(() => {
    cleanup()
    setRecording(null)
    setDurationMs(0)
    setErrorKind(null)
    setState('idle')
  }, [cleanup])

  return { state, durationMs, errorKind, recording, isSupported, start, stop, reset }
}
